'use client'

import type { Session } from '@korvo/core/types/session'
import { useEffect, useState } from 'react'

type DefaultSessionTimeoutProps = {
  timeoutMinutes?: number
  warningMinutes?: number
  initialSession?: Session | null
}

export function DefaultSessionTimeout({ 
  timeoutMinutes = 30, 
  warningMinutes = 5,
  initialSession
}: DefaultSessionTimeoutProps) {
  // Use initialSession from server-side fetch - no client-side useSession hook needed
  // This avoids SSR compilation errors with Better Auth's useSession hook
  const [session, setSession] = useState<Session | null | undefined>(initialSession)
  const [isPending, setIsPending] = useState(!initialSession)
  
  // If no initial session provided, fetch on client after mount
  useEffect(() => {
    if (initialSession !== undefined) return

    let cancelled = false
    const loadSession = async () => {
      try {
        const { authClient } = await import('@/lib/better-auth/client')
        const result = await authClient.getSession()
        if (!cancelled) {
          setSession(result.data as unknown as Session | null)
        }
      } catch {
        if (!cancelled) {
          setSession(null)
        }
      } finally {
        if (!cancelled) {
          setIsPending(false)
        }
      }
    }
    void loadSession()
    return () => {
      cancelled = true
    }
  }, [initialSession])
  
  const status = isPending ? 'loading' : (session ? 'authenticated' : 'unauthenticated')
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())

  useEffect(() => {
    if (status !== 'authenticated') return

    const timeoutMs = timeoutMinutes * 60 * 1000
    const warningMs = warningMinutes * 60 * 1000

    const recordLastActivity = () => {
      setLastActivity(Date.now())
      setShowWarning(false)
    }

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      document.addEventListener(event, recordLastActivity, true)
    })

    const checkTimeout = () => {
      const now = Date.now()
      const timeSinceActivity = now - lastActivity
      const remainingTime = timeoutMs - timeSinceActivity

      if (remainingTime <= 0) {
        const runSignOut = async () => {
          try {
            const { signOut } = await import('@/lib/better-auth/client')
            await signOut()
          } catch {
            // proceed to redirect even if signOut fails
          }
          window.location.href = '/auth/signin?reason=timeout'
        }
        void runSignOut()
        return
      }

      if (remainingTime <= warningMs) {
        // Show warning
        setTimeLeft(Math.ceil(remainingTime / 1000))
        setShowWarning(true)
      } else {
        setShowWarning(false)
      }
    }

    const interval = setInterval(checkTimeout, 1000)
    checkTimeout() // Initial check

    return () => {
      clearInterval(interval)
      events.forEach(event => {
        document.removeEventListener(event, recordLastActivity, true)
      })
    }
  }, [session, status, timeoutMinutes, warningMinutes, lastActivity])

  const handleExtendSession = () => {
    setLastActivity(Date.now())
    setShowWarning(false)
  }

  if (!showWarning || !timeLeft) return null

  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">Session Timeout Warning</h3>
          </div>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Your session will expire in{' '}
            <span className="font-semibold text-red-600">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
            . Click &quot;Stay Logged In&quot; to extend your session.
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              const runSignOut = async () => {
                try {
                  const { signOut } = await import('@/lib/better-auth/client')
                  await signOut()
                } catch {
                  // proceed to redirect even if signOut fails
                }
                window.location.href = '/auth/signin'
              }
              void runSignOut()
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Log Out
          </button>
          <button
            onClick={handleExtendSession}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  )
}

